import type { SideBarDef } from 'ag-grid-community';

import { setQuickFilter } from '@/app/slices/quickFilter';
import { CustomViews } from '@/components/common/custom-views';
import { useAppDispatch, useAppSelector } from '@/components/hooks/hook';
import type { ColDef } from '@/grid/types';

export const ROW_NUMBER_COLUMN = {
  colId: 'rowNumber',
  headerName: '#',
  valueGetter: (params) =>
    typeof params.node?.rowIndex === 'number' ? params.node.rowIndex + 1 : '',
  width: 55,
  minWidth: 55,
  maxWidth: 55,
  pinned: 'left',
  lockPosition: true,
  suppressMovable: true,
  suppressNavigable: true,
  suppressHeaderMenuButton: true,
  editable: false,
  sortable: false,
  filter: false,
  resizable: false,
} satisfies ColDef<object>;

// Every filter menu gets Apply and Clear buttons. AG Grid deep-merges
// defaultColDef.filterParams into each column's filterParams, so columns with
// their own params still get these unless they set buttons themselves.
export const defaultFilterParams = {
  buttons: ['apply', 'clear'],
  closeOnApply: true,
} satisfies ColDef<object>['filterParams'];

export const defaultColDef: ColDef<object> = {
  sortable: true,
  pivot: false,
  filter: true,
  wrapText: false,
  resizable: true,
  autoHeight: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  enableValue: false,
  enableRowGroup: true,
  enablePivot: false,
  menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
  filterParams: {
    ...defaultFilterParams,
    maxNumConditions: 1,
  },
};

export const pivotDefaultColDef: ColDef<object> = {
  sortable: true,
  filter: true,
  wrapText: false,
  resizable: true,
  autoHeight: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  enableValue: true,
  enableRowGroup: true,
  enablePivot: true,
  minWidth: 130,
  filterParams: defaultFilterParams,
};

export const defaultDetailColDef: ColDef<object> = {
  flex: 1,
  sortable: true,
  pivot: false,
  wrapText: false,
  resizable: true,
  autoHeight: false,
  wrapHeaderText: false,
  autoHeaderHeight: true,
  suppressAutoSize: false,
};

export const defaultFreshShipmentColDef: ColDef<object> = {
  sortable: true,
  pivot: false,
  filter: true,
  wrapText: false,
  resizable: true,
  autoHeight: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  enableValue: false,
  enableRowGroup: true,
  enablePivot: false,
  filterParams: defaultFilterParams,
};

export const icons = {
  'custom-views': '<span class="ag-icon ag-icon-eye"></span>',
  settings: '<span class="ag-icon ag-icon-settings"></span>',
};

export const sideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
    },
    {
      id: 'customViews',
      labelDefault: 'Custom Views',
      labelKey: 'customViews',
      iconKey: 'custom-views',
      toolPanel: CustomViews,
      toolPanelParams: {
        setQuickFilter,
        useAppDispatch,
        useAppSelector,
      },
    },
  ],
};

const lookupSideBarCache = new Map<string, SideBarDef>();

export const lookupSideBar = (viewContext: string): SideBarDef => {
  const cached = lookupSideBarCache.get(viewContext);
  if (cached) return cached;

  const config: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
      {
        id: 'customViews',
        labelDefault: 'Custom Views',
        labelKey: 'customViews',
        iconKey: 'custom-views',
        toolPanel: CustomViews,
        toolPanelParams: {
          setQuickFilter,
          useAppDispatch,
          useAppSelector,
          viewContext,
        },
      },
    ],
  };

  lookupSideBarCache.set(viewContext, config);
  return config;
};
